// Gallery Filters — artistic filters used in the Filter Gallery dialog.
// Each filter implements the Filter interface and provides a distinct
// visual effect. Filters are organized by category for the gallery UI.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function luminance(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function sampleBilinear(
  src: Uint8ClampedArray, w: number, h: number,
  fx: number, fy: number,
): [number, number, number, number] {
  const cx = Math.max(0, Math.min(w - 1, fx));
  const cy = Math.max(0, Math.min(h - 1, fy));
  const x0 = Math.floor(cx), y0 = Math.floor(cy);
  const x1 = Math.min(x0 + 1, w - 1), y1 = Math.min(y0 + 1, h - 1);
  const fracX = cx - x0, fracY = cy - y0;
  const ix = 1 - fracX, iy = 1 - fracY;
  const o00 = (y0 * w + x0) * 4, o10 = (y0 * w + x1) * 4;
  const o01 = (y1 * w + x0) * 4, o11 = (y1 * w + x1) * 4;
  const r = src[o00]! * ix * iy + src[o10]! * fracX * iy + src[o01]! * ix * fracY + src[o11]! * fracX * fracY;
  const g = src[o00 + 1]! * ix * iy + src[o10 + 1]! * fracX * iy + src[o01 + 1]! * ix * fracY + src[o11 + 1]! * fracX * fracY;
  const b = src[o00 + 2]! * ix * iy + src[o10 + 2]! * fracX * iy + src[o01 + 2]! * ix * fracY + src[o11 + 2]! * fracX * fracY;
  const a = src[o00 + 3]! * ix * iy + src[o10 + 3]! * fracX * iy + src[o01 + 3]! * ix * fracY + src[o11 + 3]! * fracX * fracY;
  return [r, g, b, a];
}

// ---------------------------------------------------------------------------
// Artistic: Cutout
// ---------------------------------------------------------------------------

export interface CutoutParams {
  numberOfLevels: number;
  edgeSimplicity: number;
  edgeFidelity: number;
}

export const Cutout: Filter<CutoutParams> = {
  id: "cutout",
  displayName: "Cutout",
  defaultParams: { numberOfLevels: 4, edgeSimplicity: 2, edgeFidelity: 3 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const levels = Math.max(2, Math.round(params.numberOfLevels));
    const fidelity = Math.max(1, Math.round(params.edgeFidelity));
    const step = 255 / (levels - 1);
    for (let i = 0; i < w * h; i++) {
      const o = i * 4;
      const r = src[o]!, g = src[o + 1]!, b = src[o + 2]!;
      const [hh, ss, ll] = rgbToHsl(r, g, b);
      const nl = Math.round(ll * (levels - 1)) / (levels - 1);
      const ns = Math.round(ss * fidelity / 3) / (fidelity / 3);
      const [nr, ng, nb] = hslToRgb(hh, Math.min(1, ns), nl);
      dst[o] = nr; dst[o + 1] = ng; dst[o + 2] = nb; dst[o + 3] = src[o + 3]!;
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Artistic: Dry Brush
// ---------------------------------------------------------------------------

export interface DryBrushParams {
  brushSize: number;
  brushDetail: number;
  texture: number;
}

export const DryBrush: Filter<DryBrushParams> = {
  id: "dry-brush",
  displayName: "Dry Brush",
  defaultParams: { brushSize: 3, brushDetail: 6, texture: 2 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const radius = Math.max(1, Math.round(params.brushSize));
    const detail = Math.max(1, Math.round(params.brushDetail));
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let rr = 0, rg = 0, rb = 0, count = 0;
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const sx = Math.min(w - 1, Math.max(0, x + dx));
            const sy = Math.min(h - 1, Math.max(0, y + dy));
            const so = (sy * w + sx) * 4;
            rr += src[so]!; rg += src[so + 1]!; rb += src[so + 2]!;
            count++;
          }
        }
        const o = (y * w + x) * 4;
        let r = rr / count, g = rg / count, b = rb / count;
        const lum = luminance(r, g, b);
        const boost = (lum > 128 ? 1.1 : 0.9) * (detail / 6);
        dst[o] = clamp8(r * boost);
        dst[o + 1] = clamp8(g * boost);
        dst[o + 2] = clamp8(b * boost);
        dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Artistic: Film Grain
// ---------------------------------------------------------------------------

export interface FilmGrainParams {
  grain: number;
  highlightArea: number;
  intensity: number;
}

let _grainSeed = 42;
function seededRandom(): number {
  _grainSeed = (_grainSeed * 16807 + 0) % 2147483647;
  return (_grainSeed - 1) / 2147483646;
}

export const FilmGrain: Filter<FilmGrainParams> = {
  id: "film-grain",
  displayName: "Film Grain",
  defaultParams: { grain: 8, highlightArea: 10, intensity: 10 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const grainAmt = params.grain / 100;
    const intensity = params.intensity / 10;
    _grainSeed = 42;
    for (let i = 0; i < w * h; i++) {
      const o = i * 4;
      const lum = luminance(src[o]!, src[o + 1]!, src[o + 2]!);
      const highlightThreshold = params.highlightArea * 2.55;
      const highlightFactor = lum > highlightThreshold ? (lum - highlightThreshold) / (255 - highlightThreshold) : 0;
      const noise = (seededRandom() - 0.5) * 255 * grainAmt * intensity * (1 - highlightFactor * 0.5);
      dst[o] = clamp8(src[o]! + noise);
      dst[o + 1] = clamp8(src[o + 1]! + noise);
      dst[o + 2] = clamp8(src[o + 2]! + noise);
      dst[o + 3] = src[o + 3]!;
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Artistic: Fresco
// ---------------------------------------------------------------------------

export interface FrescoParams {
  brushDetail: number;
  brushSize: number;
  texture: number;
}

export const Fresco: Filter<FrescoParams> = {
  id: "fresco",
  displayName: "Fresco",
  defaultParams: { brushDetail: 6, brushSize: 2, texture: 1 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const radius = Math.max(1, Math.round(params.brushSize));
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let rr = 0, rg = 0, rb = 0, count = 0;
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const sx = Math.min(w - 1, Math.max(0, x + dx));
            const sy = Math.min(h - 1, Math.max(0, y + dy));
            const so = (sy * w + sx) * 4;
            rr += src[so]!; rg += src[so + 1]!; rb += src[so + 2]!;
            count++;
          }
        }
        const o = (y * w + x) * 4;
        let r = rr / count, g = rg / count, b = rb / count;
        const lum = luminance(r, g, b);
        const contrast = 1 + (params.texture / 10);
        r = clamp8(((r / 255 - 0.5) * contrast + 0.5) * 255);
        g = clamp8(((g / 255 - 0.5) * contrast + 0.5) * 255);
        b = clamp8(((b / 255 - 0.5) * contrast + 0.5) * 255);
        const [hh, ss, ll] = rgbToHsl(r, g, b);
        const [nr, ng, nb] = hslToRgb(hh, ss * 0.8, Math.min(1, ll * 1.1));
        dst[o] = nr; dst[o + 1] = ng; dst[o + 2] = nb; dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Artistic: Neon Glow
// ---------------------------------------------------------------------------

export interface NeonGlowParams {
  glowSize: number;
  glowBrightness: number;
  glowColor: number;
}

export const NeonGlow: Filter<NeonGlowParams> = {
  id: "neon-glow",
  displayName: "Neon Glow",
  defaultParams: { glowSize: 5, glowBrightness: 15, glowColor: 128 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const radius = Math.max(1, Math.round(params.glowSize));
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let rr = 0, rg = 0, rb = 0, count = 0;
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const sx = Math.min(w - 1, Math.max(0, x + dx));
            const sy = Math.min(h - 1, Math.max(0, y + dy));
            const so = (sy * w + sx) * 4;
            rr += src[so]!; rg += src[so + 1]!; rb += src[so + 2]!;
            count++;
          }
        }
        const o = (y * w + x) * 4;
        const avgR = rr / count, avgG = rg / count, avgB = rb / count;
        const glow = params.glowBrightness / 10;
        const colorShift = params.glowColor / 255;
        dst[o] = clamp8(src[o]! + (avgR * glow * colorShift));
        dst[o + 1] = clamp8(src[o + 1]! + (avgG * glow * (1 - colorShift)));
        dst[o + 2] = clamp8(src[o + 2]! + (avgB * glow * colorShift));
        dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Artistic: Paint Daubs
// ---------------------------------------------------------------------------

export interface PaintDaubsParams {
  brushSize: number;
  sharpness: number;
  brushType: number;
}

export const PaintDaubs: Filter<PaintDaubsParams> = {
  id: "paint-daubs",
  displayName: "Paint Daubs",
  defaultParams: { brushSize: 5, sharpness: 5, brushType: 0 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const radius = Math.max(1, Math.round(params.brushSize));
    const sharp = params.sharpness / 5;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let rr = 0, rg = 0, rb = 0, count = 0;
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const sx = Math.min(w - 1, Math.max(0, x + dx));
            const sy = Math.min(h - 1, Math.max(0, y + dy));
            const so = (sy * w + sx) * 4;
            rr += src[so]!; rg += src[so + 1]!; rb += src[so + 2]!;
            count++;
          }
        }
        const o = (y * w + x) * 4;
        let r = rr / count, g = rg / count, b = rb / count;
        const lum = luminance(r, g, b);
        const [hh, ss, ll] = rgbToHsl(src[o]!, src[o + 1]!, src[o + 2]!);
        const [nr, ng, nb] = hslToRgb(hh, Math.min(1, ss * sharp), ll);
        dst[o] = clamp8((r + nr) / 2);
        dst[o + 1] = clamp8((g + ng) / 2);
        dst[o + 2] = clamp8((b + nb) / 2);
        dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Artistic: Plastic Wrap
// ---------------------------------------------------------------------------

export interface PlasticWrapParams {
  highlightStrength: number;
  detail: number;
  smoothness: number;
}

export const PlasticWrap: Filter<PlasticWrapParams> = {
  id: "plastic-wrap",
  displayName: "Plastic Wrap",
  defaultParams: { highlightStrength: 12, detail: 8, smoothness: 8 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const radius = Math.max(1, Math.round(params.smoothness / 3));
    const highlight = params.highlightStrength / 10;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let rr = 0, rg = 0, rb = 0, count = 0;
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const sx = Math.min(w - 1, Math.max(0, x + dx));
            const sy = Math.min(h - 1, Math.max(0, y + dy));
            const so = (sy * w + sx) * 4;
            rr += src[so]!; rg += src[so + 1]!; rb += src[so + 2]!;
            count++;
          }
        }
        const o = (y * w + x) * 4;
        const avgR = rr / count, avgG = rg / count, avgB = rb / count;
        const srcLum = luminance(src[o]!, src[o + 1]!, src[o + 2]!);
        const avgLum = luminance(avgR, avgG, avgB);
        const highlightBoost = srcLum > avgLum ? highlight * (srcLum - avgLum) / 255 : 0;
        dst[o] = clamp8(avgR + highlightBoost * 255);
        dst[o + 1] = clamp8(avgG + highlightBoost * 255);
        dst[o + 2] = clamp8(avgB + highlightBoost * 255);
        dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Artistic: Poster Edges
// ---------------------------------------------------------------------------

export interface PosterEdgesParams {
  edgeThickness: number;
  edgeIntensity: number;
  posterization: number;
}

export const PosterEdges: Filter<PosterEdgesParams> = {
  id: "poster-edges",
  displayName: "Poster Edges",
  defaultParams: { edgeThickness: 2, edgeIntensity: 1, posterization: 4 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const levels = Math.max(2, Math.round(params.posterization));
    const step = 255 / (levels - 1);
    const edgeThresh = (10 - params.edgeIntensity) * 25;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const o = (y * w + x) * 4;
        const l = luminance(src[o]!, src[o + 1]!, src[o + 2]!);
        const lRight = luminance(src[o + 4]!, src[o + 5]!, src[o + 6]!);
        const lDown = luminance(src[(y + 1) * w * 4 + x * 4]!, src[(y + 1) * w * 4 + x * 4 + 1]!, src[(y + 1) * w * 4 + x * 4 + 2]!);
        const edgeH = Math.abs(l - lRight);
        const edgeV = Math.abs(l - lDown);
        const edge = Math.max(edgeH, edgeV);
        let r = Math.round(Math.round(src[o]! / step) * step);
        let g = Math.round(Math.round(src[o + 1]! / step) * step);
        let b = Math.round(Math.round(src[o + 2]! / step) * step);
        if (edge > edgeThresh) {
          const dark = params.edgeThickness * 15;
          r = Math.max(0, r - dark);
          g = Math.max(0, g - dark);
          b = Math.max(0, b - dark);
        }
        dst[o] = clamp8(r); dst[o + 1] = clamp8(g); dst[o + 2] = clamp8(b); dst[o + 3] = src[o + 3]!;
      }
    }
    for (let x = 0; x < w; x++) {
      const o = x * 4;
      dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
      const o2 = ((h - 1) * w + x) * 4;
      dst[o2] = src[o2]!; dst[o2 + 1] = src[o2 + 1]!; dst[o2 + 2] = src[o2 + 2]!; dst[o2 + 3] = src[o2 + 3]!;
    }
    for (let y = 0; y < h; y++) {
      const o = (y * w) * 4;
      dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
      const o2 = (y * w + w - 1) * 4;
      dst[o2] = src[o2]!; dst[o2 + 1] = src[o2 + 1]!; dst[o2 + 2] = src[o2 + 2]!; dst[o2 + 3] = src[o2 + 3]!;
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Artistic: Rough Pastels
// ---------------------------------------------------------------------------

export interface RoughPastelsParams {
  strokeLength: number;
  strokeDetail: number;
  texture: number;
  scaling: number;
  relief: number;
  lightDirection: number;
}

export const RoughPastels: Filter<RoughPastelsParams> = {
  id: "rough-pastels",
  displayName: "Rough Pastels",
  defaultParams: { strokeLength: 15, strokeDetail: 6, texture: 1, scaling: 50, relief: 20, lightDirection: 0 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const radius = Math.max(1, Math.round(params.strokeLength / 5));
    const reliefAmt = params.relief / 100;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let rr = 0, rg = 0, rb = 0, count = 0;
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const sx = Math.min(w - 1, Math.max(0, x + dx));
            const sy = Math.min(h - 1, Math.max(0, y + dy));
            const so = (sy * w + sx) * 4;
            rr += src[so]!; rg += src[so + 1]!; rb += src[so + 2]!;
            count++;
          }
        }
        const o = (y * w + x) * 4;
        let r = rr / count, g = rg / count, b = rb / count;
        const lum = luminance(r, g, b);
        const texNoise = (seededRandom() - 0.5) * params.texture * 20;
        const relief = (lum - 128) * reliefAmt;
        dst[o] = clamp8(r + texNoise + relief);
        dst[o + 1] = clamp8(g + texNoise + relief);
        dst[o + 2] = clamp8(b + texNoise + relief);
        dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Artistic: Smudge Stick
// ---------------------------------------------------------------------------

export interface SmudgeStickParams {
  strokeLength: number;
  sprayAmount: number;
  strokePressure: number;
}

export const SmudgeStick: Filter<SmudgeStickParams> = {
  id: "smudge-stick",
  displayName: "Smudge Stick",
  defaultParams: { strokeLength: 8, sprayAmount: 5, strokePressure: 5 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const radius = Math.max(1, Math.round(params.strokeLength / 3));
    const pressure = params.strokePressure / 5;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let rr = 0, rg = 0, rb = 0, count = 0;
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const sx = Math.min(w - 1, Math.max(0, x + dx));
            const sy = Math.min(h - 1, Math.max(0, y + dy));
            const so = (sy * w + sx) * 4;
            const weight = 1 - (Math.abs(dx) + Math.abs(dy)) / (radius * 2);
            rr += src[so]! * weight; rg += src[so + 1]! * weight; rb += src[so + 2]! * weight;
            count += weight;
          }
        }
        const o = (y * w + x) * 4;
        const blurR = rr / count, blurG = rg / count, blurB = rb / count;
        dst[o] = clamp8(src[o]! * (1 - pressure) + blurR * pressure);
        dst[o + 1] = clamp8(src[o + 1]! * (1 - pressure) + blurG * pressure);
        dst[o + 2] = clamp8(src[o + 2]! * (1 - pressure) + blurB * pressure);
        dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Artistic: Sponge
// ---------------------------------------------------------------------------

export interface SpongeParams {
  simplicity: number;
  smoothness: number;
  scaling: number;
}

export const Sponge: Filter<SpongeParams> = {
  id: "sponge",
  displayName: "Sponge",
  defaultParams: { simplicity: 3, smoothness: 4, scaling: 3 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const radius = Math.max(1, Math.round(params.smoothness));
    const levels = Math.max(2, Math.round(params.simplicity));
    const step = 255 / (levels - 1);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let rr = 0, rg = 0, rb = 0, count = 0;
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const sx = Math.min(w - 1, Math.max(0, x + dx));
            const sy = Math.min(h - 1, Math.max(0, y + dy));
            const so = (sy * w + sx) * 4;
            rr += src[so]!; rg += src[so + 1]!; rb += src[so + 2]!;
            count++;
          }
        }
        const o = (y * w + x) * 4;
        const r = Math.round(Math.round((rr / count) / step) * step);
        const g = Math.round(Math.round((rg / count) / step) * step);
        const b = Math.round(Math.round((rb / count) / step) * step);
        dst[o] = clamp8(r); dst[o + 1] = clamp8(g); dst[o + 2] = clamp8(b); dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Artistic: Underpainting
// ---------------------------------------------------------------------------

export interface UnderpaintingParams {
  brushSize: number;
  textureCoverage: number;
  texture: number;
  scaling: number;
  relief: number;
  lightDirection: number;
}

export const Underpainting: Filter<UnderpaintingParams> = {
  id: "underpainting",
  displayName: "Underpainting",
  defaultParams: { brushSize: 3, textureCoverage: 30, texture: 1, scaling: 50, relief: 10, lightDirection: 0 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const radius = Math.max(1, Math.round(params.brushSize));
    const coverage = params.textureCoverage / 100;
    const reliefAmt = params.relief / 100;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let rr = 0, rg = 0, rb = 0, count = 0;
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const sx = Math.min(w - 1, Math.max(0, x + dx));
            const sy = Math.min(h - 1, Math.max(0, y + dy));
            const so = (sy * w + sx) * 4;
            rr += src[so]!; rg += src[so + 1]!; rb += src[so + 2]!;
            count++;
          }
        }
        const o = (y * w + x) * 4;
        let r = rr / count, g = rg / count, b = rb / count;
        const lum = luminance(r, g, b);
        const texNoise = (seededRandom() - 0.5) * 255 * coverage * params.texture / 3;
        const relief = (lum - 128) * reliefAmt;
        const [hh, ss, ll] = rgbToHsl(r, g, b);
        const [nr, ng, nb] = hslToRgb(hh, ss * 0.7, Math.min(1, ll + relief / 255));
        dst[o] = clamp8(nr + texNoise);
        dst[o + 1] = clamp8(ng + texNoise);
        dst[o + 2] = clamp8(nb + texNoise);
        dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Artistic: Watercolor
// ---------------------------------------------------------------------------

export interface WatercolorParams {
  brushDetail: number;
  shadowIntensity: number;
  texture: number;
}

export const Watercolor: Filter<WatercolorParams> = {
  id: "watercolor",
  displayName: "Watercolor",
  defaultParams: { brushDetail: 8, shadowIntensity: 2, texture: 1 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const radius = Math.max(1, Math.round(params.brushDetail / 3));
    const shadow = params.shadowIntensity / 2;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let rr = 0, rg = 0, rb = 0, count = 0;
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const sx = Math.min(w - 1, Math.max(0, x + dx));
            const sy = Math.min(h - 1, Math.max(0, y + dy));
            const so = (sy * w + sx) * 4;
            rr += src[so]!; rg += src[so + 1]!; rb += src[so + 2]!;
            count++;
          }
        }
        const o = (y * w + x) * 4;
        let r = rr / count, g = rg / count, b = rb / count;
        const lum = luminance(r, g, b);
        const shadowFactor = lum < 128 ? (128 - lum) / 128 * shadow : 0;
        const [hh, ss, ll] = rgbToHsl(r, g, b);
        const [nr, ng, nb] = hslToRgb(hh, Math.min(1, ss * 0.6), Math.max(0, ll - shadowFactor * 0.3));
        dst[o] = clamp8(nr); dst[o + 1] = clamp8(ng); dst[o + 2] = clamp8(nb); dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Brush Strokes: Crosshatch
// ---------------------------------------------------------------------------

export interface CrosshatchParams {
  strokeLength: number;
  sharpness: number;
  intensity: number;
}

export const Crosshatch: Filter<CrosshatchParams> = {
  id: "crosshatch",
  displayName: "Crosshatch",
  defaultParams: { strokeLength: 8, sharpness: 3, intensity: 2 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const spacing = Math.max(2, Math.round(params.strokeLength / 2));
    const intensity = params.intensity * 30;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const o = (y * w + x) * 4;
        const lum = luminance(src[o]!, src[o + 1]!, src[o + 2]!);
        const darken = (255 - lum) / 255 * intensity;
        const isHatch = (x % spacing < 2) || (y % spacing < 2) || ((x + y) % spacing < 2) || ((x - y + h) % spacing < 2);
        const factor = isHatch ? Math.min(1, darken / 100 * params.sharpness) : 0;
        dst[o] = clamp8(src[o]! * (1 - factor));
        dst[o + 1] = clamp8(src[o + 1]! * (1 - factor));
        dst[o + 2] = clamp8(src[o + 2]! * (1 - factor));
        dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Brush Strokes: Dark Strokes
// ---------------------------------------------------------------------------

export interface DarkStrokesParams {
  balance: number;
  strokeLength: number;
  strokePressure: number;
}

export const DarkStrokes: Filter<DarkStrokesParams> = {
  id: "dark-strokes",
  displayName: "Dark Strokes",
  defaultParams: { balance: 5, strokeLength: 8, strokePressure: 5 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const radius = Math.max(1, Math.round(params.strokeLength / 4));
    const pressure = params.strokePressure / 5;
    const balance = params.balance / 5;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let rr = 0, rg = 0, rb = 0, count = 0;
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const sx = Math.min(w - 1, Math.max(0, x + dx));
            const sy = Math.min(h - 1, Math.max(0, y + dy));
            const so = (sy * w + sx) * 4;
            rr += src[so]!; rg += src[so + 1]!; rb += src[so + 2]!;
            count++;
          }
        }
        const o = (y * w + x) * 4;
        const lum = luminance(src[o]!, src[o + 1]!, src[o + 2]!);
        const blurR = rr / count, blurG = rg / count, blurB = rb / count;
        const darkFactor = (1 - lum / 255) * pressure * balance;
        dst[o] = clamp8(src[o]! * (1 - darkFactor) + blurR * darkFactor);
        dst[o + 1] = clamp8(src[o + 1]! * (1 - darkFactor) + blurG * darkFactor);
        dst[o + 2] = clamp8(src[o + 2]! * (1 - darkFactor) + blurB * darkFactor);
        dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Brush Strokes: Ink Outlines
// ---------------------------------------------------------------------------

export interface InkOutlinesParams {
  strokeLength: number;
  darkIntensity: number;
  strokePressure: number;
}

export const InkOutlines: Filter<InkOutlinesParams> = {
  id: "ink-outlines",
  displayName: "Ink Outlines",
  defaultParams: { strokeLength: 8, darkIntensity: 5, strokePressure: 5 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const intensity = params.darkIntensity * 25;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const o = (y * w + x) * 4;
        const l = luminance(src[o]!, src[o + 1]!, src[o + 2]!);
        const lR = luminance(src[o + 4]!, src[o + 5]!, src[o + 6]!);
        const lD = luminance(src[(y + 1) * w * 4 + x * 4]!, src[(y + 1) * w * 4 + x * 4 + 1]!, src[(y + 1) * w * 4 + x * 4 + 2]!);
        const edge = Math.max(Math.abs(l - lR), Math.abs(l - lD));
        const ink = edge > 15 ? intensity * (edge / 255) : 0;
        dst[o] = clamp8(src[o]! - ink);
        dst[o + 1] = clamp8(src[o + 1]! - ink);
        dst[o + 2] = clamp8(src[o + 2]! - ink);
        dst[o + 3] = src[o + 3]!;
      }
    }
    for (let i = 0; i < w * h; i++) {
      const o = i * 4;
      const lum = luminance(dst[o]!, dst[o + 1]!, dst[o + 2]!);
      if (lum > 128) {
        dst[o] = clamp8(255 - (255 - dst[o]!) * 0.5);
        dst[o + 1] = clamp8(255 - (255 - dst[o + 1]!) * 0.5);
        dst[o + 2] = clamp8(255 - (255 - dst[o + 2]!) * 0.5);
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Sketch: Bas Relief
// ---------------------------------------------------------------------------

export interface BasReliefParams {
  detail: number;
  smoothness: number;
  lightDirection: number;
}

export const BasRelief: Filter<BasReliefParams> = {
  id: "bas-relief",
  displayName: "Bas Relief",
  defaultParams: { detail: 6, smoothness: 6, lightDirection: 0 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const detail = params.detail * 2;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const o = (y * w + x) * 4;
        const sx = Math.min(w - 1, x + 1);
        const sy = Math.min(h - 1, y + 1);
        const l1 = luminance(src[o]!, src[o + 1]!, src[o + 2]!);
        const l2 = luminance(src[(y * w + sx) * 4]!, src[(y * w + sx) * 4 + 1]!, src[(y * w + sx) * 4 + 2]!);
        const l3 = luminance(src[(sy * w + x) * 4]!, src[(sy * w + x) * 4 + 1]!, src[(sy * w + x) * 4 + 2]!);
        const emboss = ((l2 - l1) + (l3 - l1)) * detail / 100 + 128;
        const val = clamp8(emboss);
        dst[o] = val; dst[o + 1] = val; dst[o + 2] = val; dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Sketch: Charcoal
// ---------------------------------------------------------------------------

export interface CharcoalParams {
  charcoalThickness: number;
  charcoalDetail: number;
  lightDirection: number;
}

export const Charcoal: Filter<CharcoalParams> = {
  id: "charcoal",
  displayName: "Charcoal",
  defaultParams: { charcoalThickness: 4, charcoalDetail: 6, lightDirection: 0 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const thickness = params.charcoalThickness * 5;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const o = (y * w + x) * 4;
        const l = luminance(src[o]!, src[o + 1]!, src[o + 2]!);
        const lR = luminance(src[o + 4]!, src[o + 5]!, src[o + 6]!);
        const lD = luminance(src[(y + 1) * w * 4 + x * 4]!, src[(y + 1) * w * 4 + x * 4 + 1]!, src[(y + 1) * w * 4 + x * 4 + 2]!);
        const edge = Math.abs(l - lR) + Math.abs(l - lD);
        const val = edge > 20 ? clamp8(255 - edge * thickness / 10) : clamp8(l + 30);
        dst[o] = val; dst[o + 1] = val; dst[o + 2] = val; dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Sketch: Graphic Pen
// ---------------------------------------------------------------------------

export interface GraphicPenParams {
  strokeLength: number;
  darkIntensity: number;
  strokePressure: number;
}

export const GraphicPen: Filter<GraphicPenParams> = {
  id: "graphic-pen",
  displayName: "Graphic Pen",
  defaultParams: { strokeLength: 10, darkIntensity: 5, strokePressure: 5 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const spacing = Math.max(2, Math.round(20 - params.strokeLength));
    const intensity = params.darkIntensity * 25;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const o = (y * w + x) * 4;
        const lum = luminance(src[o]!, src[o + 1]!, src[o + 2]!);
        const isStroke = (x + y) % spacing < 2 || (x - y + h) % spacing < 2;
        const dark = isStroke ? intensity * (1 - lum / 255) : 0;
        const val = lum < 128 ? clamp8(lum - dark) : clamp8(255 - (255 - lum) * 0.3);
        dst[o] = val; dst[o + 1] = val; dst[o + 2] = val; dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Sketch: Halftone Pattern
// ---------------------------------------------------------------------------

export interface HalftonePatternParams {
  shape: number;
  contrast: number;
  size: number;
}

export const HalftonePattern: Filter<HalftonePatternParams> = {
  id: "halftone-pattern",
  displayName: "Halftone Pattern",
  defaultParams: { shape: 0, contrast: 7, size: 4 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const cellSize = Math.max(2, Math.round(params.size));
    const contrast = params.contrast / 5;
    for (let cy = 0; cy < h; cy += cellSize) {
      for (let cx = 0; cx < w; cx += cellSize) {
        let sum = 0, count = 0;
        for (let dy = 0; dy < cellSize && cy + dy < h; dy++) {
          for (let dx = 0; dx < cellSize && cx + dx < w; dx++) {
            const o = ((cy + dy) * w + (cx + dx)) * 4;
            sum += luminance(src[o]!, src[o + 1]!, src[o + 2]!);
            count++;
          }
        }
        const avg = sum / count;
        const dotRadius = (1 - avg / 255) * cellSize / 2 * contrast;
        for (let dy = 0; dy < cellSize && cy + dy < h; dy++) {
          for (let dx = 0; dx < cellSize && cx + dx < w; dx++) {
            const dist = Math.sqrt((dx - cellSize / 2) ** 2 + (dy - cellSize / 2) ** 2);
            const val = dist < dotRadius ? 0 : 255;
            const o = ((cy + dy) * w + (cx + dx)) * 4;
            dst[o] = val; dst[o + 1] = val; dst[o + 2] = val; dst[o + 3] = src[o + 3]!;
          }
        }
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Stylize: Accented Edges
// ---------------------------------------------------------------------------

export interface AccentedEdgesParams {
  edgeWidth: number;
  edgeBrightness: number;
  smoothness: number;
}

export const AccentedEdges: Filter<AccentedEdgesParams> = {
  id: "accented-edges",
  displayName: "Accented Edges",
  defaultParams: { edgeWidth: 3, edgeBrightness: 30, smoothness: 5 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const radius = Math.max(1, Math.round(params.smoothness / 2));
    const edgeBright = params.edgeBrightness * 5;
    const edgeW = params.edgeWidth * 15;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const o = (y * w + x) * 4;
        const l = luminance(src[o]!, src[o + 1]!, src[o + 2]!);
        const lR = luminance(src[o + 4]!, src[o + 5]!, src[o + 6]!);
        const lD = luminance(src[(y + 1) * w * 4 + x * 4]!, src[(y + 1) * w * 4 + x * 4 + 1]!, src[(y + 1) * w * 4 + x * 4 + 2]!);
        const edge = Math.max(Math.abs(l - lR), Math.abs(l - lD));
        let rr = 0, rg = 0, rb = 0, count = 0;
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const sx = Math.min(w - 1, Math.max(0, x + dx));
            const sy = Math.min(h - 1, Math.max(0, y + dy));
            const so = (sy * w + sx) * 4;
            rr += src[so]!; rg += src[so + 1]!; rb += src[so + 2]!;
            count++;
          }
        }
        const avgR = rr / count, avgG = rg / count, avgB = rb / count;
        if (edge > 20) {
          const bright = Math.min(255, edgeBright * edge / 255);
          dst[o] = clamp8(avgR + bright);
          dst[o + 1] = clamp8(avgG + bright);
          dst[o + 2] = clamp8(avgB + bright);
        } else {
          dst[o] = clamp8(avgR); dst[o + 1] = clamp8(avgG); dst[o + 2] = clamp8(avgB);
        }
        dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Stylize: Grain
// ---------------------------------------------------------------------------

export interface GrainParams {
  intensity: number;
  contrast: number;
  grainType: number;
}

export const GrainFilter: Filter<GrainParams> = {
  id: "grain",
  displayName: "Grain",
  defaultParams: { intensity: 20, contrast: 5, grainType: 0 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const intensity = params.intensity / 100;
    const contrast = 1 + params.contrast / 10;
    _grainSeed = 123;
    for (let i = 0; i < w * h; i++) {
      const o = i * 4;
      const noise = (seededRandom() - 0.5) * 255 * intensity;
      dst[o] = clamp8(((src[o]! / 255 - 0.5) * contrast + 0.5) * 255 + noise);
      dst[o + 1] = clamp8(((src[o + 1]! / 255 - 0.5) * contrast + 0.5) * 255 + noise);
      dst[o + 2] = clamp8(((src[o + 2]! / 255 - 0.5) * contrast + 0.5) * 255 + noise);
      dst[o + 3] = src[o + 3]!;
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Stylize: Texturizer
// ---------------------------------------------------------------------------

export interface TexturizerParams {
  texture: number;
  scaling: number;
  relief: number;
  lightDirection: number;
}

export const Texturizer: Filter<TexturizerParams> = {
  id: "texturizer",
  displayName: "Texturizer",
  defaultParams: { texture: 0, scaling: 100, relief: 5, lightDirection: 0 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const reliefAmt = params.relief / 10;
    const scale = params.scaling / 100;
    _grainSeed = 77;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const o = (y * w + x) * 4;
        const tex = (seededRandom() - 0.5) * 255 * reliefAmt;
        const lum = luminance(src[o]!, src[o + 1]!, src[o + 2]!);
        const [hh, ss, ll] = rgbToHsl(src[o]!, src[o + 1]!, src[o + 2]!);
        const newL = Math.min(1, Math.max(0, ll + tex / 255 * 0.2));
        const [nr, ng, nb] = hslToRgb(hh, ss, newL);
        dst[o] = clamp8(nr); dst[o + 1] = clamp8(ng); dst[o + 2] = clamp8(nb); dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Distort: Twirl (standalone for gallery)
// ---------------------------------------------------------------------------

export interface TwirlParams {
  angle: number;
}

export const Twirl: Filter<TwirlParams> = {
  id: "twirl",
  displayName: "Twirl",
  defaultParams: { angle: 180 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const halfW = w / 2, halfH = h / 2;
    const angle = params.angle * Math.PI / 180;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const nx = (x / w) * 2 - 1;
        const ny = (y / h) * 2 - 1;
        const r = Math.sqrt(nx * nx + ny * ny);
        const a = angle * (1 - r);
        const cosA = Math.cos(a), sinA = Math.sin(a);
        const snx = nx * cosA - ny * sinA;
        const sny = nx * sinA + ny * cosA;
        const sx = snx * halfW + halfW;
        const sy = sny * halfH + halfH;
        const [rr, rg, rb, ra] = sampleBilinear(src, w, h, sx, sy);
        const o = (y * w + x) * 4;
        dst[o] = clamp8(rr); dst[o + 1] = clamp8(rg); dst[o + 2] = clamp8(rb); dst[o + 3] = clamp8(ra);
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Distort: Wave (standalone for gallery)
// ---------------------------------------------------------------------------

export interface WaveParams {
  numGenerators: number;
  wavelength: number;
  amplitude: number;
  scale: number;
}

export const WaveFilter: Filter<WaveParams> = {
  id: "wave",
  displayName: "Wave",
  defaultParams: { numGenerators: 3, wavelength: 20, amplitude: 10, scale: 100 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const generators = Math.max(1, Math.round(params.numGenerators));
    const waveLen = Math.max(1, params.wavelength);
    const amp = params.amplitude;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let dx = 0, dy = 0;
        for (let g = 0; g < generators; g++) {
          dx += Math.sin(y / waveLen * Math.PI * 2 + g) * amp;
          dy += Math.cos(x / waveLen * Math.PI * 2 + g) * amp;
        }
        const sx = Math.min(w - 1, Math.max(0, x + dx));
        const sy = Math.min(h - 1, Math.max(0, y + dy));
        const [rr, rg, rb, ra] = sampleBilinear(src, w, h, sx, sy);
        const o = (y * w + x) * 4;
        dst[o] = clamp8(rr); dst[o + 1] = clamp8(rg); dst[o + 2] = clamp8(rb); dst[o + 3] = clamp8(ra);
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Distort: ZigZag (standalone for gallery)
// ---------------------------------------------------------------------------

export interface ZigZagParams {
  amount: number;
  ridges: number;
}

export const ZigZagFilter: Filter<ZigZagParams> = {
  id: "zigzag",
  displayName: "ZigZag",
  defaultParams: { amount: 10, ridges: 10 },

  apply(input, params, _sel) {
    const { width: w, height: h } = input;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data, dst = out.data;
    const halfW = w / 2, halfH = h / 2;
    const amount = params.amount / 100;
    const ridges = params.ridges;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const nx = (x - halfW) / halfW;
        const ny = (y - halfH) / halfH;
        const r = Math.sqrt(nx * nx + ny * ny);
        const angle = Math.atan2(ny, nx) + Math.sin(r * ridges * Math.PI) * amount;
        const sx = halfW + Math.cos(angle) * r * halfW;
        const sy = halfH + Math.sin(angle) * r * halfH;
        const [rr, rg, rb, ra] = sampleBilinear(src, w, h, sx, sy);
        const o = (y * w + x) * 4;
        dst[o] = clamp8(rr); dst[o + 1] = clamp8(rg); dst[o + 2] = clamp8(rb); dst[o + 3] = clamp8(ra);
      }
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Gallery filter registry
// ---------------------------------------------------------------------------

export interface GalleryFilterEntry {
  id: string;
  name: string;
  category: string;
  filter: Filter<unknown>;
  defaultParams: Record<string, number | boolean | string>;
  paramDefs: Array<{ key: string; label: string; min: number; max: number; default: number; step?: number }>;
}

export const galleryFilterEntries: GalleryFilterEntry[] = [
  {
    id: "cutout", name: "Cutout", category: "Artistic",
    filter: Cutout as Filter<unknown>,
    defaultParams: { numberOfLevels: 4, edgeSimplicity: 2, edgeFidelity: 3 },
    paramDefs: [
      { key: "numberOfLevels", label: "Number of Levels", min: 2, max: 12, default: 4 },
      { key: "edgeSimplicity", label: "Edge Simplicity", min: 0, max: 10, default: 2 },
      { key: "edgeFidelity", label: "Edge Fidelity", min: 1, max: 5, default: 3 },
    ],
  },
  {
    id: "dry-brush", name: "Dry Brush", category: "Artistic",
    filter: DryBrush as Filter<unknown>,
    defaultParams: { brushSize: 3, brushDetail: 6, texture: 2 },
    paramDefs: [
      { key: "brushSize", label: "Brush Size", min: 0, max: 10, default: 3 },
      { key: "brushDetail", label: "Brush Detail", min: 0, max: 10, default: 6 },
      { key: "texture", label: "Texture", min: 1, max: 3, default: 2 },
    ],
  },
  {
    id: "film-grain", name: "Film Grain", category: "Artistic",
    filter: FilmGrain as Filter<unknown>,
    defaultParams: { grain: 8, highlightArea: 10, intensity: 10 },
    paramDefs: [
      { key: "grain", label: "Grain", min: 0, max: 20, default: 8 },
      { key: "highlightArea", label: "Highlight Area", min: 0, max: 40, default: 10 },
      { key: "intensity", label: "Intensity", min: 0, max: 20, default: 10 },
    ],
  },
  {
    id: "fresco", name: "Fresco", category: "Artistic",
    filter: Fresco as Filter<unknown>,
    defaultParams: { brushDetail: 6, brushSize: 2, texture: 1 },
    paramDefs: [
      { key: "brushDetail", label: "Brush Detail", min: 0, max: 10, default: 6 },
      { key: "brushSize", label: "Brush Size", min: 0, max: 10, default: 2 },
      { key: "texture", label: "Texture", min: 1, max: 3, default: 1 },
    ],
  },
  {
    id: "neon-glow", name: "Neon Glow", category: "Artistic",
    filter: NeonGlow as Filter<unknown>,
    defaultParams: { glowSize: 5, glowBrightness: 15, glowColor: 128 },
    paramDefs: [
      { key: "glowSize", label: "Glow Size", min: 1, max: 25, default: 5 },
      { key: "glowBrightness", label: "Glow Brightness", min: 0, max: 50, default: 15 },
      { key: "glowColor", label: "Glow Color", min: 0, max: 255, default: 128 },
    ],
  },
  {
    id: "paint-daubs", name: "Paint Daubs", category: "Artistic",
    filter: PaintDaubs as Filter<unknown>,
    defaultParams: { brushSize: 5, sharpness: 5, brushType: 0 },
    paramDefs: [
      { key: "brushSize", label: "Brush Size", min: 1, max: 20, default: 5 },
      { key: "sharpness", label: "Sharpness", min: 0, max: 10, default: 5 },
      { key: "brushType", label: "Brush Type", min: 0, max: 5, default: 0 },
    ],
  },
  {
    id: "plastic-wrap", name: "Plastic Wrap", category: "Artistic",
    filter: PlasticWrap as Filter<unknown>,
    defaultParams: { highlightStrength: 12, detail: 8, smoothness: 8 },
    paramDefs: [
      { key: "highlightStrength", label: "Highlight Strength", min: 0, max: 50, default: 12 },
      { key: "detail", label: "Detail", min: 1, max: 15, default: 8 },
      { key: "smoothness", label: "Smoothness", min: 1, max: 15, default: 8 },
    ],
  },
  {
    id: "poster-edges", name: "Poster Edges", category: "Artistic",
    filter: PosterEdges as Filter<unknown>,
    defaultParams: { edgeThickness: 2, edgeIntensity: 1, posterization: 4 },
    paramDefs: [
      { key: "edgeThickness", label: "Edge Thickness", min: 0, max: 5, default: 2 },
      { key: "edgeIntensity", label: "Edge Intensity", min: 0, max: 5, default: 1 },
      { key: "posterization", label: "Posterization", min: 2, max: 10, default: 4 },
    ],
  },
  {
    id: "rough-pastels", name: "Rough Pastels", category: "Artistic",
    filter: RoughPastels as Filter<unknown>,
    defaultParams: { strokeLength: 15, strokeDetail: 6, texture: 1, scaling: 50, relief: 20, lightDirection: 0 },
    paramDefs: [
      { key: "strokeLength", label: "Stroke Length", min: 5, max: 25, default: 15 },
      { key: "strokeDetail", label: "Stroke Detail", min: 1, max: 10, default: 6 },
      { key: "texture", label: "Texture", min: 1, max: 3, default: 1 },
      { key: "scaling", label: "Scaling", min: 20, max: 200, default: 50 },
      { key: "relief", label: "Relief", min: 0, max: 50, default: 20 },
    ],
  },
  {
    id: "smudge-stick", name: "Smudge Stick", category: "Artistic",
    filter: SmudgeStick as Filter<unknown>,
    defaultParams: { strokeLength: 8, sprayAmount: 5, strokePressure: 5 },
    paramDefs: [
      { key: "strokeLength", label: "Stroke Length", min: 0, max: 20, default: 8 },
      { key: "sprayAmount", label: "Spray Amount", min: 0, max: 10, default: 5 },
      { key: "strokePressure", label: "Stroke Pressure", min: 0, max: 10, default: 5 },
    ],
  },
  {
    id: "sponge", name: "Sponge", category: "Artistic",
    filter: Sponge as Filter<unknown>,
    defaultParams: { simplicity: 3, smoothness: 4, scaling: 3 },
    paramDefs: [
      { key: "simplicity", label: "Simplicity", min: 1, max: 10, default: 3 },
      { key: "smoothness", label: "Smoothness", min: 0, max: 10, default: 4 },
      { key: "scaling", label: "Scaling", min: 1, max: 10, default: 3 },
    ],
  },
  {
    id: "underpainting", name: "Underpainting", category: "Artistic",
    filter: Underpainting as Filter<unknown>,
    defaultParams: { brushSize: 3, textureCoverage: 30, texture: 1, scaling: 50, relief: 10, lightDirection: 0 },
    paramDefs: [
      { key: "brushSize", label: "Brush Size", min: 0, max: 10, default: 3 },
      { key: "textureCoverage", label: "Texture Coverage", min: 0, max: 100, default: 30 },
      { key: "texture", label: "Texture", min: 1, max: 3, default: 1 },
      { key: "scaling", label: "Scaling", min: 20, max: 200, default: 50 },
      { key: "relief", label: "Relief", min: 0, max: 50, default: 10 },
    ],
  },
  {
    id: "watercolor", name: "Watercolor", category: "Artistic",
    filter: Watercolor as Filter<unknown>,
    defaultParams: { brushDetail: 8, shadowIntensity: 2, texture: 1 },
    paramDefs: [
      { key: "brushDetail", label: "Brush Detail", min: 1, max: 14, default: 8 },
      { key: "shadowIntensity", label: "Shadow Intensity", min: 0, max: 10, default: 2 },
      { key: "texture", label: "Texture", min: 1, max: 3, default: 1 },
    ],
  },
  {
    id: "crosshatch", name: "Crosshatch", category: "Brush Strokes",
    filter: Crosshatch as Filter<unknown>,
    defaultParams: { strokeLength: 8, sharpness: 3, intensity: 2 },
    paramDefs: [
      { key: "strokeLength", label: "Stroke Length", min: 3, max: 25, default: 8 },
      { key: "sharpness", label: "Sharpness", min: 0, max: 10, default: 3 },
      { key: "intensity", label: "Intensity", min: 0, max: 5, default: 2 },
    ],
  },
  {
    id: "dark-strokes", name: "Dark Strokes", category: "Brush Strokes",
    filter: DarkStrokes as Filter<unknown>,
    defaultParams: { balance: 5, strokeLength: 8, strokePressure: 5 },
    paramDefs: [
      { key: "balance", label: "Balance", min: 0, max: 10, default: 5 },
      { key: "strokeLength", label: "Stroke Length", min: 1, max: 25, default: 8 },
      { key: "strokePressure", label: "Stroke Pressure", min: 0, max: 10, default: 5 },
    ],
  },
  {
    id: "ink-outlines", name: "Ink Outlines", category: "Brush Strokes",
    filter: InkOutlines as Filter<unknown>,
    defaultParams: { strokeLength: 8, darkIntensity: 5, strokePressure: 5 },
    paramDefs: [
      { key: "strokeLength", label: "Stroke Length", min: 1, max: 25, default: 8 },
      { key: "darkIntensity", label: "Dark Intensity", min: 0, max: 10, default: 5 },
      { key: "strokePressure", label: "Stroke Pressure", min: 0, max: 10, default: 5 },
    ],
  },
  {
    id: "bas-relief", name: "Bas Relief", category: "Sketch",
    filter: BasRelief as Filter<unknown>,
    defaultParams: { detail: 6, smoothness: 6, lightDirection: 0 },
    paramDefs: [
      { key: "detail", label: "Detail", min: 1, max: 15, default: 6 },
      { key: "smoothness", label: "Smoothness", min: 1, max: 15, default: 6 },
      { key: "lightDirection", label: "Light", min: 0, max: 3, default: 0 },
    ],
  },
  {
    id: "charcoal", name: "Charcoal", category: "Sketch",
    filter: Charcoal as Filter<unknown>,
    defaultParams: { charcoalThickness: 4, charcoalDetail: 6, lightDirection: 0 },
    paramDefs: [
      { key: "charcoalThickness", label: "Thickness", min: 1, max: 10, default: 4 },
      { key: "charcoalDetail", label: "Detail", min: 1, max: 10, default: 6 },
      { key: "lightDirection", label: "Light", min: 0, max: 3, default: 0 },
    ],
  },
  {
    id: "graphic-pen", name: "Graphic Pen", category: "Sketch",
    filter: GraphicPen as Filter<unknown>,
    defaultParams: { strokeLength: 10, darkIntensity: 5, strokePressure: 5 },
    paramDefs: [
      { key: "strokeLength", label: "Stroke Length", min: 5, max: 25, default: 10 },
      { key: "darkIntensity", label: "Dark Intensity", min: 0, max: 10, default: 5 },
      { key: "strokePressure", label: "Stroke Pressure", min: 0, max: 10, default: 5 },
    ],
  },
  {
    id: "halftone-pattern", name: "Halftone Pattern", category: "Sketch",
    filter: HalftonePattern as Filter<unknown>,
    defaultParams: { shape: 0, contrast: 7, size: 4 },
    paramDefs: [
      { key: "shape", label: "Shape", min: 0, max: 2, default: 0 },
      { key: "contrast", label: "Contrast", min: 0, max: 10, default: 7 },
      { key: "size", label: "Size", min: 2, max: 12, default: 4 },
    ],
  },
  {
    id: "accented-edges", name: "Accented Edges", category: "Stylize",
    filter: AccentedEdges as Filter<unknown>,
    defaultParams: { edgeWidth: 3, edgeBrightness: 30, smoothness: 5 },
    paramDefs: [
      { key: "edgeWidth", label: "Edge Width", min: 1, max: 10, default: 3 },
      { key: "edgeBrightness", label: "Brightness", min: 0, max: 50, default: 30 },
      { key: "smoothness", label: "Smoothness", min: 1, max: 15, default: 5 },
    ],
  },
  {
    id: "grain", name: "Grain", category: "Stylize",
    filter: GrainFilter as Filter<unknown>,
    defaultParams: { intensity: 20, contrast: 5, grainType: 0 },
    paramDefs: [
      { key: "intensity", label: "Intensity", min: 0, max: 100, default: 20 },
      { key: "contrast", label: "Contrast", min: 0, max: 10, default: 5 },
      { key: "grainType", label: "Type", min: 0, max: 5, default: 0 },
    ],
  },
  {
    id: "texturizer", name: "Texturizer", category: "Stylize",
    filter: Texturizer as Filter<unknown>,
    defaultParams: { texture: 0, scaling: 100, relief: 5, lightDirection: 0 },
    paramDefs: [
      { key: "texture", label: "Texture", min: 0, max: 3, default: 0 },
      { key: "scaling", label: "Scaling", min: 50, max: 200, default: 100 },
      { key: "relief", label: "Relief", min: 0, max: 50, default: 5 },
    ],
  },
  {
    id: "twirl", name: "Twirl", category: "Distort",
    filter: Twirl as Filter<unknown>,
    defaultParams: { angle: 180 },
    paramDefs: [
      { key: "angle", label: "Angle", min: -360, max: 360, default: 180 },
    ],
  },
  {
    id: "wave", name: "Wave", category: "Distort",
    filter: WaveFilter as Filter<unknown>,
    defaultParams: { numGenerators: 3, wavelength: 20, amplitude: 10, scale: 100 },
    paramDefs: [
      { key: "numGenerators", label: "Generators", min: 1, max: 15, default: 3 },
      { key: "wavelength", label: "Wavelength", min: 5, max: 100, default: 20 },
      { key: "amplitude", label: "Amplitude", min: 0, max: 100, default: 10 },
    ],
  },
  {
    id: "zigzag", name: "ZigZag", category: "Distort",
    filter: ZigZagFilter as Filter<unknown>,
    defaultParams: { amount: 10, ridges: 10 },
    paramDefs: [
      { key: "amount", label: "Amount", min: -100, max: 100, default: 10 },
      { key: "ridges", label: "Ridges", min: 1, max: 30, default: 10 },
    ],
  },
];

export function getGalleryFiltersByCategory(): Map<string, GalleryFilterEntry[]> {
  const map = new Map<string, GalleryFilterEntry[]>();
  for (const entry of galleryFilterEntries) {
    if (!map.has(entry.category)) map.set(entry.category, []);
    map.get(entry.category)!.push(entry);
  }
  return map;
}

export function getGalleryFilterById(id: string): GalleryFilterEntry | undefined {
  return galleryFilterEntries.find(e => e.id === id);
}
